import * as React from 'react'
import { createContext, useContext } from 'react'

import Settings from 'app/config/appConfig'
import {
  fillPlaceholders,
  flattenTranslations,
  missingTranslation,
  TranslateData,
} from './translations'

export type { TranslateData }

export type Language = { code: string; name: string }

export type TranslateOptions = {
  onMissingTranslation?: (info: {
    translationId: string
    languageCode: string
  }) => string
}

export type TranslateFunction = (
  key: string,
  data?: TranslateData | null,
  options?: TranslateOptions,
) => string

type LocalizeContextValue = {
  translate: TranslateFunction
  /** `translate` zawężone do stringa - domyślne wejście dla nowego kodu. */
  t: TranslateFunction
  /** Jak `t`, ale zamiast komunikatu o braku tłumaczenia zwraca podaną wartość. */
  tSafe: (key: string, defaultValue: string) => string
  activeLanguage?: Language
  languages: Language[]
  setActiveLanguage: (code: string) => void
}

const LocalizeReactContext = createContext<LocalizeContextValue | null>(null)

const LANGUAGES: Language[] = Settings.languages.map((language) => ({
  name: language.name,
  code: language.code,
}))

const DEFAULT_LANGUAGE = Settings.language

const getCookie = (name: string) => {
  const prefix = `${name}=`
  const parts = decodeURIComponent(document.cookie).split(';')
  for (const part of parts) {
    const entry = part.trimStart()
    if (entry.indexOf(prefix) === 0) {
      return entry.substring(prefix.length)
    }
  }
  return ''
}

const translationsCache = new Map<string, Record<string, string>>()

const loadTranslations = (code: string) => {
  const cached = translationsCache.get(code)
  if (cached) {
    return cached
  }
  // Ścieżka budowana ze zmiennej celowo: rspack tworzy z tego moduł kontekstowy
  // i wpina wszystkie pliki tłumaczeń, więc zmiana języka nie wymaga sieci.
  // `import()` nie zadziała - musi być synchronicznie, w trakcie renderu.
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const file = require(`app/translations/${code}.resources.json`)
  const flat = flattenTranslations(file)
  translationsCache.set(code, flat)
  return flat
}

const resolveInitialCode = () => {
  const fromCookie = getCookie('lang')
  return LANGUAGES.some((language) => language.code === fromCookie)
    ? fromCookie
    : DEFAULT_LANGUAGE
}

/**
 * Dostawca tłumaczeń - montowany raz, w korzeniu aplikacji (`main.tsx`).
 *
 * Wcześniej to był most na `react-localize-redux` (`withLocalize`); teraz trzyma
 * cały stan sam: aktywny język, wczytany plik `*.resources.json` i podmianę
 * `${...}` w tekstach. Poza `useLocalize` nic w aplikacji tego nie widzi.
 */
export const LocalizeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [code, setCode] = React.useState(resolveInitialCode)

  const translations = React.useMemo(() => loadTranslations(code), [code])

  // `t`/`tSafe` trafiają do tablic zależności `useMemo`/`useCallback` w setkach
  // miejsc - muszą zmieniać tożsamość tylko przy zmianie języka, inaczej cała
  // memoizacja etykiet przestaje cokolwiek dawać.
  const translate = React.useCallback<TranslateFunction>(
    (key, data, options) => {
      const text = translations[key]
      if (text == null) {
        return options?.onMissingTranslation
          ? options.onMissingTranslation({
              translationId: key,
              languageCode: code,
            })
          : missingTranslation(key, code)
      }
      return fillPlaceholders(text, data)
    },
    [translations, code],
  )

  const tSafe = React.useCallback(
    (key: string, defaultValue: string) =>
      translate(key, null, { onMissingTranslation: () => defaultValue }),
    [translate],
  )

  const setActiveLanguage = React.useCallback((next: string) => {
    setCode((current) =>
      LANGUAGES.some((language) => language.code === next) ? next : current,
    )
  }, [])

  const value = React.useMemo<LocalizeContextValue>(
    () => ({
      translate,
      t: translate,
      tSafe,
      activeLanguage: LANGUAGES.find((language) => language.code === code),
      languages: LANGUAGES,
      setActiveLanguage,
    }),
    [translate, tSafe, code, setActiveLanguage],
  )

  return (
    <LocalizeReactContext.Provider value={value}>
      {children}
    </LocalizeReactContext.Provider>
  )
}

export const useLocalize = () => {
  const ctx = useContext(LocalizeReactContext)
  if (!ctx) throw new Error('useLocalize must be used inside <LocalizeProvider>')
  return ctx
}

const FALLBACK_TRANSLATIONS: Record<string, string> = {
  'common.select': 'Select',
  'common.search': 'Search',
  'common.no-results': 'No results',
  'common.loading': 'Loading...',
  'common.no-data': 'No data',
  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.confirm': 'Confirm',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.add': 'Add',
  'common.close': 'Close',
  'common.yes': 'Yes',
  'common.no': 'No',
}

export const useLocalizeSafe = () => {
  const ctx = useContext(LocalizeReactContext)
  if (!ctx) {
    return {
      t: (key: string) => FALLBACK_TRANSLATIONS[key] || key.split('.').pop() || key,
      tSafe: (_key: string, defaultValue: string) => defaultValue,
    } as LocalizeContextValue
  }
  return ctx
}

/**
 * `useLocalize` w formie HOC-a - dla komponentów klasowych, które nie mogą wywołać
 * hooka. Wstrzykuje ten sam kształt propsów, który dawał `withLocalize`
 * z `react-localize-redux`, więc podmiana jest jeden do jednego.
 */
export function withLocalizeProps<P extends object>(
  Wrapped: React.ComponentType<P>,
) {
  const WithLocalizeProps = React.forwardRef<unknown, any>((props, ref) => {
    const localize = useLocalize()
    return <Wrapped ref={ref as any} {...(props as P)} {...localize} />
  })

  WithLocalizeProps.displayName = `withLocalizeProps(${
    (Wrapped as any).displayName || (Wrapped as any).name || 'Component'
  })`

  return WithLocalizeProps
}

/** Kształt propsów wstrzykiwanych przez `withLocalizeProps`. */
export type LocalizeProps = LocalizeContextValue
